import { T, Num } from 'gt-react';
import {
  XIcon,
  ArrowsClockwiseIcon,
  CaretUpIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import { ChatActionIcon as AgentIcon } from '@erp/ui/chat-action-icon';
import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SuggestedActionCard } from './SuggestedActionCard';
import { EmailDraftCard } from './EmailDraftCard';
import type { SuggestedActionWithStatus } from './types';
import type { EmailDraftSummary } from '@erp/rpc';

interface SuggestedActionsPanelProps {
  actions: SuggestedActionWithStatus[];
  emailDraft?: EmailDraftSummary | null;
  onApprove: (action: SuggestedActionWithStatus) => void;
  onReject: (action: SuggestedActionWithStatus) => void;
  onDismiss: (action: SuggestedActionWithStatus) => void;
  onDismissAll?: () => void;
  onRerun?: () => void;
  onReviewDraft?: () => void;
  onDismissDraft?: () => void;
  approvingActionId?: string | null;
  className?: string;
}

export function SuggestedActionsPanel({
  actions,
  emailDraft,
  onApprove,
  onReject,
  onDismiss,
  onDismissAll,
  onRerun,
  onReviewDraft,
  onDismissDraft,
  approvingActionId = null,
  className,
}: SuggestedActionsPanelProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isDraftDismissed, setIsDraftDismissed] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const visibleActions = useMemo(
    () =>
      actions.filter((a) => a.status === 'pending' && !dismissedIds.has(a.id)),
    [actions, dismissedIds],
  );

  const showDraft =
    emailDraft && emailDraft.status === 'draft' && !isDraftDismissed;
  const pendingCount = visibleActions.length + (showDraft ? 1 : 0);

  if (pendingCount === 0) {
    return null;
  }

  function handleDismiss(action: SuggestedActionWithStatus) {
    setDismissedIds((prev) => new Set([...prev, action.id]));
    onDismiss(action);
  }

  function handleDismissAll() {
    const allIds = new Set(visibleActions.map((a) => a.id));
    setDismissedIds(allIds);
    setIsDraftDismissed(true);
    onDismissAll?.();
  }

  function handleDismissDraft() {
    setIsDraftDismissed(true);
    onDismissDraft?.();
  }

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border-x border-t border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900',
        className,
      )}
    >
      {/* Collapsible header */}
      <div
        className={cn(
          'flex shrink-0 cursor-pointer items-center justify-between px-3 py-2',
          !isCollapsed && 'border-b border-stone-200 dark:border-stone-700',
        )}
        onClick={() => setIsCollapsed((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800">
            <AgentIcon state="agent" size={20} />
          </div>
          <span className="text-sm font-medium text-stone-900 dark:text-stone-100">
            <T context="AI suggestions panel header with count">
              AI Suggestions (<Num>{pendingCount}</Num>)
            </T>
          </span>
          {isCollapsed ? (
            <CaretDownIcon className="h-3.5 w-3.5 text-stone-500" />
          ) : (
            <CaretUpIcon className="h-3.5 w-3.5 text-stone-500" />
          )}
        </div>
        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {onRerun && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRerun}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-stone-900 dark:hover:text-stone-100"
            >
              <ArrowsClockwiseIcon className="mr-1 h-3 w-3" />
              <T context="button, rerun AI suggestions">Re-run</T>
            </Button>
          )}
          {onDismissAll && pendingCount > 1 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismissAll}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
            >
              <XIcon className="mr-1 h-3 w-3" />
              <T context="button, dismiss all AI suggestions">Dismiss All</T>
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable items list (hidden when collapsed) */}
      {!isCollapsed && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
          {/* Email draft card (above action cards) */}
          {showDraft && onReviewDraft && (
            <EmailDraftCard
              draft={emailDraft}
              onReviewDraft={onReviewDraft}
              onDismiss={handleDismissDraft}
            />
          )}

          {/* Suggested action cards */}
          {visibleActions.map((action) => (
            <SuggestedActionCard
              key={action.id}
              action={action}
              onApprove={onApprove}
              onReject={onReject}
              onDismiss={handleDismiss}
              isApplying={approvingActionId === action.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
